/**
 * Substrate profiles loader with lazy loading from external JSON
 * Optimized for bundle size by fetching data dynamically
 */

import type { SubstrateProfile } from "../types/substrate";

// Cache for loaded profiles
let profilesCache: Map<string, SubstrateProfile[]> | null = null;

// Load substrate profiles from external JSON
async function loadSubstrateProfiles(): Promise<
  Map<string, SubstrateProfile[]>
> {
  if (profilesCache) {
    return profilesCache;
  }

  try {
    const response = await fetch("/data/substrate-profiles.json");
    const data = await response.json();

    profilesCache = new Map(Object.entries(data));
    return profilesCache;
  } catch (error) {
    console.error("Failed to load substrate profiles:", error);
    return new Map();
  }
}

// Get profiles by category with lazy loading
export async function getSubstrateProfiles(
  category?: string,
): Promise<SubstrateProfile[]> {
  const profiles = await loadSubstrateProfiles();

  if (category) {
    return profiles.get(category) || [];
  }

  // Return all profiles if no category specified
  const allProfiles: SubstrateProfile[] = [];
  for (const categoryProfiles of profiles.values()) {
    allProfiles.push(...categoryProfiles);
  }
  return allProfiles;
}

// Get specific substrate profile by ID
export async function getSubstrateProfile(
  id: string,
): Promise<SubstrateProfile | null> {
  const profiles = await loadSubstrateProfiles();

  for (const categoryProfiles of profiles.values()) {
    const profile = categoryProfiles.find((p) => p.id === id);
    if (profile) return profile;
  }

  return null;
}

// Get available categories
export async function getSubstrateCategories(): Promise<string[]> {
  const profiles = await loadSubstrateProfiles();
  return Array.from(profiles.keys());
}

// Legacy exports for backward compatibility (empty arrays)
export const PAPER_PROFILES: SubstrateProfile[] = [];
export const METALLIC_PROFILES: SubstrateProfile[] = [];
export const PLASTIC_PROFILES: SubstrateProfile[] = [];
export const FABRIC_PROFILES: SubstrateProfile[] = [];

// Substrate categories for testing
export const SUBSTRATE_CATEGORIES = {
  paper: ["glossy", "matte", "uncoated"],
  metallic: ["aluminum", "copper", "silver"],
  plastic: ["pvc", "polyethylene", "polypropylene"],
  fabric: ["cotton", "silk", "polyester"],
};

// Stub exports for test compatibility (these are placeholders)
// TODO: Implement actual registry functionality if needed
export function initializeSubstrateRegistry(): void {
  // Stub: In new implementation, registry is auto-initialized on first access
}

export function getSubstrateMetadata(): SubstrateProfile[] {
  // Stub: Return sample profiles for backward compatibility
  return SAMPLE_SUBSTRATE_PROFILES;
}

export function getSubstratesByCategory(category: string): SubstrateProfile[] {
  // Stub: Return sample profiles filtered by category
  return SAMPLE_SUBSTRATE_PROFILES.filter((p) => p.category === category);
}

// Sample profiles for testing (minimal set to avoid bundle bloat)
export const SAMPLE_SUBSTRATE_PROFILES: SubstrateProfile[] = [
  {
    id: "paper-glossy-coated",
    version: "1.0.0",
    name: "Glossy Coated Paper",
    category: "paper",
    manufacturer: "Generic Paper Co.",
    brightness: 88,
    glossiness: 85,
    absorptionCoefficients: {
      cyan: 0.75,
      magenta: 0.7,
      yellow: 0.65,
      black: 0.8,
    },
    baseColor: { hex: "#F8F8F8", rgb: { r: 248, g: 248, b: 248 } },
    tacLimit: 320,
    inkLimits: { cyan: 85, magenta: 85, yellow: 85, black: 90 },
    dotGain: { cyan: 15, magenta: 15, yellow: 15, black: 20 },
    // TODO: Fix ColorTransformMatrix type mismatch - currently expects object with matrix/type/inputSpace/outputSpace
    // colorTransform should match ColorTransformMatrix interface when properly implemented
    // TODO: Move description/applications/environmentalFactors to metadata object to match SubstrateProfile interface
    metadata: {
      description: "Standard glossy coated paper for high-quality printing",
      applications: ["brochures", "magazines", "catalogs"],
      environmentalConditions: { humidity: 45, temperature: 22 },
    },
  },
];
